import * as ko from 'knockout';

import { FeatureFlag, FeatureToggleService } from '@/features/feature-flags';
import { Instance as CompanyService } from '@/legacy/services/company';
import { Instance as UserService } from '@/legacy/services/user';
import { VerificationStatusIdentityViewModel } from '@/legacy/services/verification/verification';
import { combineResults, observableToAsyncResult, Result } from '@/utils/result';

import {
  companyVerification,
  companyVerificationCompleted,
  companyVerificationInProgress,
  ownershipInformation,
  userVerification,
  userVerificationCompleted,
  userVerificationFailed,
  userVerificationInProgress,
} from '../verification';

/**
 * Checks if the user is part of the Persona onboarding cohort for use when
 * there is no company - because there is no company profile to check for the
 * backend flag.
 */
export const isPersonaEnabled = observableToAsyncResult(UserService.UserId, (userId) =>
  FeatureToggleService.isFeatureEnabled(FeatureFlag.SPR5_24_ONBOARDING_PERSONA, userId)
);

export const isPartOfPersonaCohort = combineResults(
  [isPersonaEnabled],
  (isFeatureFlagEnabled) =>
    isFeatureFlagEnabled || CompanyService.profile()?.isSubjectToPersonaOnboarding()
);

/**
 * Checks to see if the company is part of the Persona onboarding cohort.
 * Ignores the feature flag state.
 */
export const isPersonaActive = combineResults(
  [userVerificationCompleted, companyVerificationCompleted],
  (userVerificationResult, companyVerificationResult) => {
    const isAccountingFirm = CompanyService.profile()?.isAccountingFirm();
    const hasAccountingFirmCompanyId = Boolean(CompanyService.profile()?.accountingFirmCompanyId());
    const isCompanySubjectToOnboarding =
      CompanyService.profile()?.isSubjectToPersonaOnboarding() &&
      !isAccountingFirm &&
      !hasAccountingFirmCompanyId;
    const isUserAndCompanyVerified = userVerificationResult && companyVerificationResult;
    return isCompanySubjectToOnboarding && !isUserAndCompanyVerified;
  }
);

const isKybSubmittingUserHack = ko.observable<Result<boolean>>(undefined);

export const isKybSubmittingUser = combineResults(
  [companyVerification, isKybSubmittingUserHack] as const,
  (companyVerificationResult, hack) => {
    if (hack) {
      return hack;
    }
    return companyVerificationResult?.submittedByUserId === UserService.UserId();
  },
  { write: isKybSubmittingUserHack }
);

export const isUbo = combineResults(
  [ownershipInformation],
  (ownershipInformationResult) =>
    ownershipInformationResult?.some(
      (owner) => owner.email?.toLowerCase() === UserService.profile()?.email().toLowerCase()
    ) ?? false
);

export const isKycStep = combineResults(
  [
    companyVerificationCompleted,
    companyVerificationInProgress,
    isUbo,
    userVerification,
    userVerificationFailed,
    userVerificationCompleted,
    userVerificationInProgress,
  ],
  (
    isCompanyVerificationCompleted,
    isCompanyVerificationInProgress,
    isUserUbo,
    userVerificationResult: VerificationStatusIdentityViewModel,
    hasUserVerificationFailed,
    isUserVerificationCompleted,
    isUserVerificationInProgress
  ) => {
    const isCompanyVerificationCompletedOrInProgress =
      isCompanyVerificationCompleted || isCompanyVerificationInProgress;
    const isUboUserVerificationIncomplete = !(
      hasUserVerificationFailed ||
      userVerificationResult?.isIdentitySubmitted ||
      userVerificationResult?.isIdentityVerified
    );
    const isNonUboUserVerificationIncomplete = !(
      hasUserVerificationFailed ||
      isUserVerificationCompleted ||
      isUserVerificationInProgress
    );
    return (
      isCompanyVerificationCompletedOrInProgress &&
      (isUserUbo ? isUboUserVerificationIncomplete : isNonUboUserVerificationIncomplete)
    );
  }
);
